import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MyTripsSection = ({ trips, requests, onRefresh, currentLanguage }) => {
  const navigate = useNavigate();

  const translations = {
    en: {
      myTrips: 'My Trips',
      noTrips: 'No active trips',
      createTrip: 'Create Trip',
      manageTrips: 'Manage Trips',
      matchingRequests: 'matching requests',
      capacity: 'Capacity',
      dates: 'Dates'
    },
    ko: {
      myTrips: '내 여행',
      noTrips: '활성 여행 없음',
      createTrip: '여행 생성',
      manageTrips: '여행 관리',
      matchingRequests: '일치하는 요청',
      capacity: '용량',
      dates: '날짜'
    }
  };

  const t = translations?.[currentLanguage];

  const tripsWithMatching = trips?.map(trip => {
    const matchingRequests = requests?.filter(request => {
      const routeMatch = trip?.fromCountry === request?.originCountry && 
                        trip?.toCountry === request?.destinationCountry;
      const categoryMatch = trip?.allowedCategories?.includes(request?.category);
      const dateMatch = new Date(request?.deliveryDate) >= new Date(trip?.departureDate) &&
                       new Date(request?.deliveryDate) <= new Date(trip?.returnDate);
      return routeMatch && categoryMatch && dateMatch;
    });

    return {
      ...trip,
      matchingRequestsCount: matchingRequests?.length
    };
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Plane" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            {t?.myTrips}
          </h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Settings"
          iconPosition="left"
          onClick={() => navigate('/traveler-trip-creation')}
        >
          {t?.manageTrips}
        </Button>
      </div>

      {tripsWithMatching?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Plane" size={24} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-muted-foreground mb-4">{t?.noTrips}</p>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => navigate('/traveler-trip-creation')}
          >
            {t?.createTrip}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tripsWithMatching?.map((trip) => (
            <div
              key={trip?.id}
              className="relative bg-muted/30 rounded-lg p-4 hover:shadow-elevation-md transition-base"
            >
              {trip?.matchingRequestsCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Icon name="Bell" size={12} />
                  {trip?.matchingRequestsCount} {t?.matchingRequests}
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Icon name="MapPin" size={16} color="var(--color-primary)" />
                <span className="text-sm font-semibold text-foreground">
                  {trip?.fromCountry} → {trip?.toCountry}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>
                    {formatDate(trip?.departureDate)} - {formatDate(trip?.returnDate)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Package" size={14} />
                  <span>{trip?.capacityKg}kg {t?.capacity}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {trip?.allowedCategories?.slice(0, 3)?.map((category, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded"
                    >
                      {category}
                    </span>
                  ))}
                  {trip?.allowedCategories?.length > 3 && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                      +{trip?.allowedCategories?.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTripsSection;